import type { PermissionGroupDto, Privilege } from '@shared/services/apiService';

/** `null` for a platform admin, who may grant anything; otherwise the caller's own privileges. */
export type ActorPrivileges = readonly Privilege[] | null;

export type PrivilegeSectionKey =
  | 'campaigns'
  | 'calls'
  | 'customers'
  | 'agents'
  | 'knowledgeBase'
  | 'integrations'
  | 'billing'
  | 'access';

const SECTION_ORDER: PrivilegeSectionKey[] = [
  'campaigns',
  'calls',
  'customers',
  'agents',
  'knowledgeBase',
  'integrations',
  'billing',
  'access',
];

// A Record over the generated union: a privilege added in the API breaks the build until it is
// placed in a section here.
const PRIVILEGE_SECTION: Record<Privilege, PrivilegeSectionKey> = {
  'campaign.manage': 'campaigns',
  'campaign.run': 'campaigns',
  'call.recording.read': 'calls',
  'call.test': 'calls',
  'call.supervise': 'calls',
  'call.hangup': 'calls',
  'customer.read': 'customers',
  'customer.manage': 'customers',
  'agent.manage': 'agents',
  'knowledge-base.read': 'knowledgeBase',
  'knowledge-base.manage': 'knowledgeBase',
  'sip-trunk.manage': 'integrations',
  'webhook.manage': 'integrations',
  'webhook.dispatch': 'integrations',
  'post-call-message.manage': 'integrations',
  'structured-output.manage': 'integrations',
  'group.manage': 'access',
  'user.manage': 'access',
  'billing.manage': 'billing',
};

export const ALL_PRIVILEGES = Object.keys(PRIVILEGE_SECTION) as Privilege[];

export const PRIVILEGE_SECTIONS = SECTION_ORDER.map((key) => ({
  key,
  privileges: ALL_PRIVILEGES.filter((privilege) => PRIVILEGE_SECTION[privilege] === key),
}));

/** i18n keys can't contain dots, so `call.recording.read` becomes `call_recording_read`. */
export const privilegeI18nKey = (privilege: Privilege) =>
  `accessControl.privileges.${privilege.replace(/[.-]/g, '_')}`;

export const canGrant = (actor: ActorPrivileges, privilege: Privilege) =>
  actor === null || actor.includes(privilege);

/** The API lets you add or take away a group only if you hold every privilege it carries. */
export const canAssignGroup = (
  actor: ActorPrivileges,
  group: Pick<PermissionGroupDto, 'privileges'>,
) => group.privileges.every((privilege) => canGrant(actor, privilege));

export const isBuiltIn = (group: Pick<PermissionGroupDto, 'builtIn'>) => Boolean(group.builtIn);
